import React, { useMemo } from 'react';

import { Box, Chip, Stack, Typography } from '@mui/material/';
import {
  CertificateDisplayStatus,
  CertificateDisplayStatusBorderColor,
  CertificateDisplayStatusFillColor,
  CertificateDisplayStatusTextColor,
} from 'utils/displayStatusColorMapper';
import { statusChipSx } from 'components/pages/certificates/statusChipSx';
import {
  getCertificateStatusLabel,
  getCertificateStatusTermId,
} from 'utils/certificateStatusLabel';
import { TermInfo } from 'components/UI/TermInfo';

interface StatusBadgeProps {
  className?: string;
  status: string;
  /**
   * Display text, when it differs from `status`. The default colour maps are keyed on
   * CERTIFICATE statuses (active / lost / stolen / transfer…); a caller rendering a
   * different domain — e.g. a scan outcome (VERA-476) — passes its own label and tint
   * rather than forcing its vocabulary through the certificate maps.
   */
  label?: string;
  /** Explicit tint, overriding the certificate-status colour maps. */
  colors?: { backgroundColor: string; borderColor: string; color: string };
  /**
   * Accepted but unused. Three call sites (CertificatesTeaser, ProductPageHeader,
   * ProductStatusBadge) have always passed it and it was never declared — a real type
   * error that went unreported because apps/app's typecheck aborts before reaching src/
   * (TS 4.9 cannot parse react-hook-form's TS 5.0 `const` type parameters).
   */
  isCertificateOwner?: boolean;
  /** Leading glyph, inheriting the chip's text colour. */
  icon?: React.ReactElement;
  /**
   * VERA-628: when set, the badge becomes tappable — MUI `Chip` handles the button
   * role + keyboard activation (Enter/Space) automatically once `onClick` is passed.
   * The explicit `:focus-visible` ring below is still needed because this component's
   * fixed `background`/`border` override MUI's built-in clickable hover/focus tint.
   */
  onClick?: () => void;
  /**
   * `chip` (default) — the filled pill. `dot` — a coloured dot plus a plain
   * label.
   *
   * The card uses `dot`. Measured, the chip's LABEL is 12px against the title's
   * 16px, so it was never bigger — what made it read louder than the product
   * name was the saturated FILL, which is pre-attentive in a way medium-weight
   * text is not. A dot keeps the same colour signal at a fraction of the visual
   * weight, so status stops out-shouting the thing it describes.
   *
   * ⚠️ This note used to say "the certificate PAGE keeps the chip". That is no
   * longer true in a stronger sense than a variant change: as of VRG-720 the page
   * has no ownership-status badge AT ALL (`ProductStatusBadge` was removed and
   * deleted — see `CertificatePage/index.tsx` for the privacy reasoning), and its
   * one remaining badge, `TagOpenedBadge`, passes `dot`. So `chip` has no call
   * site on the certificate page, and the "headline fact about one object"
   * argument this note once made has nothing left to apply to.
   *
   * `chip` is still the default and still used elsewhere; it is only the
   * certificate page that no longer reaches it.
   */
  variant?: 'chip' | 'dot';
}

export const StatusBadge = ({
  className,
  status,
  label,
  colors,
  icon,
  onClick,
  variant = 'chip',
}: StatusBadgeProps) => {
  const statusStyles = useMemo(() => {
    if (colors) return colors;
    const backgroundColor =
      CertificateDisplayStatusFillColor[status as CertificateDisplayStatus] || '#CFF7FE';
    const borderColor =
      CertificateDisplayStatusBorderColor[status as CertificateDisplayStatus] || '#A5EFFC';
    const color =
      CertificateDisplayStatusTextColor[status as CertificateDisplayStatus] || '#165863';
    return { backgroundColor, borderColor, color };
  }, [status, colors]);

  /*
   * The glossary entry this badge's word means — DERIVED, never passed in.
   *
   * Derived rather than an opt-in prop for the reason §42 records about the referrer
   * state: an opt-in that 35 of 37 call sites forget is not a convention, it is a
   * latent bug. A future certificate-status chip gets its `(i)` by existing.
   *
   * ## The guard is the whole of the correctness argument
   *
   * `status` is NOT reliably certificate vocabulary at this component. Three live
   * call sites pass a certificate status as a COLOUR CHANNEL for a different domain
   * and author their own word over it:
   *
   *   TagOpenedBadge        status='tampered'  label='Opened'
   *   SuspiciousActivityCard status='stolen'   label='Invalid scan'
   *   CertificatePage        status='stolen'   label='2 invalid'
   *
   * Derived unguarded, the last two would put a `(i)` beside "2 invalid" that opens
   * the definition of "Stolen" — a wrong answer delivered at the moment of
   * confusion, which is the single worst failure mode §44 names for a definition.
   *
   * So the badge only claims to be a certificate status when it is speaking entirely
   * in certificate vocabulary: no authored `label`, no `colors` override. That is the
   * same condition under which `label` below falls through to
   * `getCertificateStatusLabel` and the tint comes from the certificate colour maps —
   * i.e. the id and the word it defines always come from the same resolver pair.
   */
  const termId =
    label === undefined && colors === undefined
      ? getCertificateStatusTermId(status)
      : undefined;

  if (variant === 'dot') {
    return (
      <Stack
        direction='row'
        alignItems='center'
        spacing={0.75}
        className={className}
        onClick={onClick}
        /*
         * A click handler on a plain Stack is mouse-only: no role, no tab stop, no
         * Enter/Space. The one caller that passes `onClick` here opens a drawer
         * (VERA-628's seal sheet), so without this the sheet is unreachable by
         * keyboard — and as of VRG-720 that badge sits on its own line under the
         * product title, where it is the only control in its region.
         *
         * All three attributes are conditional on `onClick`: a decorative status
         * badge must NOT become a tab stop, which is the failure mode of adding
         * `tabIndex` unconditionally.
         */
        {...(onClick
          ? {
              role: 'button',
              tabIndex: 0,
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              },
            }
          : {})}
        sx={{
          cursor: onClick ? 'pointer' : 'default',
          ...(onClick
            ? {
                /*
                 * A 44pt HIT AREA THAT OCCUPIES NO EXTRA SPACE.
                 *
                 * The row's content is 20px tall — a 14px label and a 14px glyph —
                 * so the tappable region was 69x20 against Apple's 44pt minimum.
                 * 12px of vertical padding takes it to 44; the matching negative
                 * margins pull that padding back out of the flow, so the element
                 * still occupies 20px and nothing around it moves. Verified by
                 * measurement, not by eye: the label's y is unchanged and the
                 * element's own height is 44.
                 *
                 * Vertical only. The width is already 69px, comfortably past 44,
                 * and negative horizontal margins would let the hit area hang into
                 * the page gutter where it could swallow taps meant for the edge.
                 *
                 * Scoped to `onClick`, like the role/tabIndex above: a decorative
                 * badge has no hit area to enlarge, and giving it negative margins
                 * would quietly change the spacing of every surface that renders
                 * one — the certificates list card among them.
                 */
                py: '12px',
                my: '-12px',
                /*
                 * `tabIndex` without a visible ring is a tab stop nobody can see.
                 * Drawn on the padded box, so it traces the real hit area.
                 */
                borderRadius: '8px',
                '&:focus-visible': {
                  outline: `2px solid ${statusStyles.color}`,
                  outlineOffset: '2px',
                },
              }
            : {}),
        }}
      >
        {/*
          An explicit `icon` REPLACES the dot rather than sitting next to it.

          A dot is a generic severity marker: it says "there is a status here" and
          leaves the word to say which. Where a caller has a glyph that names the
          state outright — the open padlock on "Opened" — that glyph carries strictly
          more meaning in the same space, and showing both would be a marker for a
          thing already marked. Callers with no icon keep the dot, so the row still
          reads as one series.
        */}
        {icon ? (
          <Box
            aria-hidden
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              /*
                The chip's TEXT tone, not its border tone.
                
                The 8px dot uses `borderColor` because a dot is a colour chip — it
                only has to be seen, and the border tone is the most saturated value
                in the ramp. A glyph has to be READ: at 14px the same pale tone made
                the padlock's strokes disappear against white, which is what "hard to
                read" was. `color` is the ramp's foreground value, chosen to carry
                text on that fill, so it is the one that renders a legible line.
              */
              color: statusStyles.color,
            }}
          >
            {icon}
          </Box>
        ) : (
          <Box
            aria-hidden
            sx={{
              width: 8,
              height: 8,
              borderRadius: '999px',
              flexShrink: 0,
              // The chip's BORDER tone, not its fill: the fill is a pale wash
              // designed to sit behind text and is far too light to read as a
              // 8px dot on its own.
              backgroundColor: statusStyles.borderColor,
            }}
          />
        )}
        <Typography sx={{ fontSize: '14px', color: 'text.secondary', lineHeight: 1.4 }}>
          {label || status}
        </Typography>
      </Stack>
    );
  }

  /*
   * CHIP ONLY — the `dot` branch above deliberately renders no `(i)`, and the reason
   * is not the 14px-vs-22px weight it would add.
   *
   * `dot` is the LIST grammar (its own note above: "a marker on one of nine rows"),
   * and a glyph repeated down nine rows is how an affordance stops being read at all
   * — the same argument `ProductDetailsPanel` makes against putting one on all four
   * of its rows. The one `dot` call site that would qualify under the guard above is
   * `ProductPageHeader`, which the certificate page renders with `showStatus={false}`,
   * so it is dead today; there is no live surface to judge the treatment against. If
   * the dot ever becomes the page's headline status again, revisit it THEN, with
   * something on screen to look at.
   */
  const chip = (
    <>
      <Chip
        className={className}
        onClick={onClick}
        icon={icon}
        sx={{
          // Height / radius / padding / weight and the border-and-label colour rule all
          // come from the one shared source (VERA-628, §36) so this and ScanStatusChip
          // can't drift.
          ...statusChipSx(
            {
              fill: statusStyles.backgroundColor,
              border: statusStyles.borderColor,
              text: statusStyles.color,
            },
            // Always an AUTHORED label now (VERA-304): either the caller's, or the
            // resolver's properly-cased one. Either way the theme's blanket
            // `capitalize` on MuiChip must not apply — it would only ever mangle
            // a label that is already correctly cased.
            true,
          ),
          cursor: onClick ? 'pointer' : 'default',
          // The glyph keeps the ramp's deep tone, matching the border. Only the LABEL
          // goes black — an all-black chip would lose the status signal entirely.
          '& .MuiChip-icon': {
            color: statusStyles.color,
            marginLeft: '12px',
            marginRight: '-4px',
          },
          // VERA-628: explicit focus-visible ring — this component's fixed background
          // overrides MUI Chip's built-in clickable hover/focus tint, so keyboard users
          // need an explicit signal.
          '&.Mui-focusVisible': {
            outline: `2px solid ${statusStyles.color}`,
            outlineOffset: '1px',
          },
        }}
        // VERA-304: never render the raw enum. `getCertificateStatusLabel` collapses
        // the four in-flight transfer states to a single "Transfer" and title-cases
        // everything else, so no surface can print "Transfer_waiting_confirmation".
        // A caller with its own vocabulary (scan outcomes, VERA-476) still wins.
        label={label ?? getCertificateStatusLabel(status)}
      />
    </>
  );

  if (!termId) return chip;

  /*
   * The `(i)` is a SIBLING of the chip, never inside its label.
   *
   * `TermInfo` is a `<button>`, and the chip is itself clickable at several call
   * sites (VERA-628) — MUI gives a clickable `Chip` `role="button"`, so nesting one
   * inside the other is invalid markup and leaves the inner control unreachable to a
   * keyboard. Outside, `TermInfo`'s own `stopPropagation` is what keeps a tap on the
   * glyph from also firing the chip.
   *
   * `inline-flex` so the pair still behaves as one inline object: every call site
   * places this badge as a single item (a flex child, a `space-between` column), and
   * a block wrapper would let it claim a full row it was never given.
   */
  return (
    <Stack
      direction='row'
      alignItems='center'
      spacing={0.5}
      sx={{ display: 'inline-flex', verticalAlign: 'middle' }}
    >
      {chip}
      <TermInfo termId={termId} />
    </Stack>
  );
};
